/**
 * Plugin UI Extensions
 *
 * Extends the PluginUIContext with:
 *   - Device detection (isMobile, deviceType)
 *   - Page action registration
 *   - Entity type UI registration
 *
 * These are injected into every plugin's context so plugins can
 * register page actions and entity type metadata without importing
 * core internals directly.
 *
 * The extensions are wired up by the PluginLoader during plugin
 * initialization — plugins consume them via `context.ui.*`.
 */

import type { PageAction, PageActionContext, DeviceType, EntityTypeUI } from '../../types/page-actions';
import type { EntityTypeRegistration } from '../registries/entity-type-registry';
import type { HostUIAPI, PluginActionsAPI, PluginEventsAPI, ModalRequest, ModalResult } from '../../types/plugin-interaction';
import { usePageActionRegistry } from '../registries/page-action-registry';
import { useEntityTypeRegistry } from '../registries/entity-type-registry';
import { createPluginInteractionAPIs } from '../host-ui-api';
import type { ContentSyncAPI } from '../host-ui-api';
import { registerModalComponent as _registerModalComponent } from '../../components/PluginModalHost';
import { invocationLog } from '../interaction-invocation-log';
import { recordInteractiveActionInvocation } from '../interaction-warnings';
import {
  registerPluginCapabilities,
  enforcePluginCapabilities,
} from '../capability-enforcement';
import type { ManifestCapabilityDeclaration } from '@neuro/plugin-sdk';

// ── Extended UI Context ─────────────────────────────────────────

/**
 * UI extensions added to PluginUIContext.
 * Accessible as `context.ui` inside plugin components and
 * integration point setup functions.
 */
export interface PluginUIExtensions {
  /** Whether the current viewport is mobile-sized */
  isMobile: boolean;
  /** Classified device form factor */
  deviceType: DeviceType;

  /**
   * Register a page action (toolbar button, FAB, command palette item, etc.)
   * The pluginId is automatically scoped — plugins cannot register actions
   * under another plugin's namespace.
   */
  registerPageAction: (action: Omit<PageAction, 'pluginId'>) => void;

  /**
   * Register multiple page actions at once.
   */
  registerPageActions: (actions: Array<Omit<PageAction, 'pluginId'>>) => void;

  /**
   * Register entity type UI metadata (icon, label, color, route).
   * Extends or overrides the core entity type registry.
   */
  registerEntityTypeUI: (def: Omit<EntityTypeUI, 'pluginId'>) => void;

  // ── Wave 22.13.1: Host UI Interaction APIs ──────────────────

  /** Host UI API — open/close modals from within a plugin. */
  ui: HostUIAPI;

  /** Plugin actions API — register, defer, resolve, reject actions. */
  actions: PluginActionsAPI;

  /** Plugin events API — emit, subscribe, unsubscribe events. */
  events: PluginEventsAPI;

  /** Wave 22.13.4: Multi-plugin content synchronization API. */
  contentSync: ContentSyncAPI;

  /**
   * Register a modal component for a viewId so PluginModalHost can render it.
   * Call this during plugin setup to make `ui.openModal({ viewId })` work.
   */
  registerModalComponent: (
    viewId: string,
    component: React.ComponentType<{ onClose: (result: ModalResult) => void; [key: string]: any }>,
  ) => void;

  /**
   * Wave 22.13.5: Register a teardown callback invoked when the plugin
   * is unloaded or hot-reloaded. Use this to clean up event subscriptions,
   * contentSync subscriptions, and other resources.
   *
   * Multiple teardown callbacks can be registered — they execute in order.
   */
  onTeardown: (callback: () => void) => void;
}

// ── Teardown Registry (Wave 22.13.5) ────────────────────────────

/**
 * Global registry of plugin teardown callbacks.
 * The hot-reload service and PluginLoader invoke these when a plugin
 * is unloaded.
 */
const _pluginTeardownCallbacks = new Map<string, Array<() => void>>();

/**
 * Execute all registered teardown callbacks for a plugin.
 * Called by hot-reload service on plugin.unloaded or plugin.hotReloaded.
 */
export function executePluginTeardown(pluginId: string): void {
  const callbacks = _pluginTeardownCallbacks.get(pluginId);
  if (callbacks) {
    for (const cb of callbacks) {
      try {
        cb();
      } catch (err) {
        console.error(`[PluginTeardown] Error in teardown callback for "${pluginId}":`, err);
      }
    }
    _pluginTeardownCallbacks.delete(pluginId);
    console.debug(`[PluginTeardown] Executed ${callbacks.length} teardown callbacks for "${pluginId}"`);
  }
}

/**
 * Check if a plugin has registered any teardown callbacks.
 */
export function hasPluginTeardown(pluginId: string): boolean {
  return (_pluginTeardownCallbacks.get(pluginId)?.length ?? 0) > 0;
}

// ── Factory ─────────────────────────────────────────────────────

/**
 * Create the UI extensions object for a specific plugin.
 *
 * Called once per plugin during PluginLoader initialization.
 * The returned object is stable (does not change per render).
 */
export function createPluginUIExtensions(
  pluginId: string,
  deviceInfo: { isMobile: boolean; deviceType: DeviceType },
  capabilityDeclaration?: ManifestCapabilityDeclaration | null,
): PluginUIExtensions {
  const pageActionStore = usePageActionRegistry.getState();
  const entityTypeStore = useEntityTypeRegistry.getState();

  // Wave 22.13.6: Register capabilities for enforcement
  // Wave 11AU.2: Always register — pass undefined for plugins without declarations
  //              so the resolver creates an empty (deny-all) set rather than
  //              leaving the plugin unregistered (which would skip enforcement).
  registerPluginCapabilities(pluginId, capabilityDeclaration ?? undefined);

  // Wave 22.13.1: Create interaction APIs scoped to this plugin
  const rawAPIs = createPluginInteractionAPIs(pluginId);

  // Wave 22.13.6: Apply capability enforcement wrappers
  const interactionAPIs = enforcePluginCapabilities(pluginId, rawAPIs);

  // Wave 22.13.2: Wrap ui.openModal with context-level trace
  const tracedUI: HostUIAPI = {
    openModal(request: ModalRequest): Promise<ModalResult> {
      // Trace at the plugin-context boundary (context.ui.openModal)
      invocationLog.append({
        kind: 'ui-openModal-called',
        pluginId,
        detail: { viewId: request.viewId, props: request.props, source: 'plugin-context' },
      });

      // Track for INTERACTIVE_ACTION_NO_MODAL detection
      recordInteractiveActionInvocation(pluginId, request.viewId);

      return interactionAPIs.ui.openModal(request);
    },
    closeModal(result: ModalResult): void {
      interactionAPIs.ui.closeModal(result);
    },
  };

  // Wave 22.13.5: Action tracing is now handled entirely by host-ui-api's
  // createPluginActionsAPI — the previous double-wrap here added a redundant
  // 'action-interactive-start' trace layer on top of the 'action-invoke' trace.
  // We now delegate directly to the interaction APIs.
  const tracedActions: PluginActionsAPI = interactionAPIs.actions;

  return {
    isMobile: deviceInfo.isMobile,
    deviceType: deviceInfo.deviceType,

    // ── Wave 22.13.2: Traced Host UI Interaction APIs ───────────
    ui: tracedUI,
    actions: tracedActions,
    events: interactionAPIs.events,

    // ── Wave 22.13.4: Content Sync API ──────────────────────────
    contentSync: interactionAPIs.contentSync,

    registerModalComponent: (viewId, component) => {
      _registerModalComponent(viewId, component);
    },

    onTeardown: (callback) => {
      if (!_pluginTeardownCallbacks.has(pluginId)) {
        _pluginTeardownCallbacks.set(pluginId, []);
      }
      _pluginTeardownCallbacks.get(pluginId)!.push(callback);
    },

    registerPageAction: (action) => {
      pageActionStore.registerAction({
        ...action,
        pluginId,
      });
    },

    registerPageActions: (actions) => {
      pageActionStore.registerActions(
        actions.map((a) => ({ ...a, pluginId })),
      );
    },

    registerEntityTypeUI: (def) => {
      const registration: EntityTypeRegistration = {
        entityType: def.entityType,
        icon: def.icon,
        label: def.label,
        color: def.color,
        pluginId,
        route: def.staticRoute,
        mobileIcon: def.mobileIcon,
        routeBuilder: def.route,
      };
      entityTypeStore.registerEntityType(registration);
    },
  };
}

// ── Re-export types for convenience ─────────────────────────────

export type { PageAction, PageActionContext, DeviceType, EntityTypeUI };
